import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ordersService } from '@/services';
import { ApiError } from '@/services/api';
import { useAuthStore } from '@/stores/auth.store';
import type { Order, OrderItem, OrderStatus } from '@/types/order';
import type { UserRole } from '@/types/auth';
import { formatPrice, toNumber } from '@/utils/format';
import { operationalOrderStatusLabel } from '@/utils/orderOperationalDisplay';
import { Badge } from '@/components/ui';

function backToListTarget(
  order: Order | undefined,
  user: { id: string; role: UserRole } | null,
): { path: string; labelKey: string } {
  if (user?.role === 'ADMINISTRATOR') {
    return { path: '/admin/orders', labelKey: 'orders.backToAdminOrders' };
  }
  if (order && user && order.buyerId === user.id) {
    return { path: '/orders', labelKey: 'orders.backToList' };
  }
  if (!user || user.role === 'CUSTOMER') {
    return { path: '/orders', labelKey: 'orders.backToList' };
  }
  return { path: '/seller/orders', labelKey: 'orders.backToSalesList' };
}

function statusLabel(status: OrderStatus, t: (key: string) => string): string {
  const key = `orders.status_${status}`;
  const translated = t(key);
  return translated === key ? status : translated;
}

function statusVariant(status: OrderStatus): 'success' | 'warning' | 'danger' | 'neutral' {
  if (status === 'PAGADA') return 'success';
  if (status === 'PAGO_PENDIENTE') return 'warning';
  if (status === 'CANCELADA') return 'danger';
  return 'neutral';
}

function lineTotalCop(item: OrderItem): number {
  return toNumber(item.unitPriceCop) * item.quantity;
}

function lineTotalDisplay(item: OrderItem): number {
  return toNumber(item.unitPriceDisplay) * item.quantity;
}

const linkClass = 'text-brand-600 hover:text-brand-400 hover:underline';

export function OrderDetailPage(): JSX.Element {
  const { orderId } = useParams<{ orderId: string }>();
  const { t } = useTranslation('common');
  const user = useAuthStore((s) => s.user);

  const { data: order, isLoading, error, isError } = useQuery({
    queryKey: ['orders', orderId],
    queryFn: () => ordersService.getOrderById(orderId!),
    enabled: Boolean(orderId),
  });

  if (!orderId) {
    const { path, labelKey } = backToListTarget(undefined, user);
    return (
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 lg:px-8">
        <p className="font-sans text-red-700">{t('orders.notFound')}</p>
        <Link to={path} className={`mt-4 inline-block ${linkClass}`}>
          {t(labelKey)}
        </Link>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center px-4">
        <p className="font-sans text-brand-700">{t('common.loading')}</p>
      </div>
    );
  }

  if (isError && error instanceof ApiError) {
    if (error.status === 404) {
      const { path, labelKey } = backToListTarget(undefined, user);
      return (
        <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 lg:px-8">
          <p className="font-sans text-brand-950">{t('orders.notFound')}</p>
          <Link to={path} className={`mt-4 inline-block ${linkClass}`}>
            {t(labelKey)}
          </Link>
        </div>
      );
    }
    if (error.status === 403) {
      const { path, labelKey } = backToListTarget(undefined, user);
      return (
        <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 lg:px-8">
          <p className="font-sans text-brand-950">{t('orders.accessDenied')}</p>
          <Link to={path} className={`mt-4 inline-block ${linkClass}`}>
            {t(labelKey)}
          </Link>
        </div>
      );
    }
  }

  if (isError || !order) {
    const { path, labelKey } = backToListTarget(undefined, user);
    return (
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 lg:px-8">
        <p className="font-sans text-red-700">{t('orders.loadError')}</p>
        <Link to={path} className={`mt-4 inline-block ${linkClass}`}>
          {t(labelKey)}
        </Link>
      </div>
    );
  }

  const showDisplayTotal =
    order.displayAmount != null &&
    order.displayCurrency &&
    order.displayCurrency !== 'COP';

  const { path: backPath, labelKey: backLabelKey } = backToListTarget(order, user);
  const sellerViewTotal =
    order.sellerSubtotalCop != null ? order.sellerSubtotalCop : toNumber(order.totalAmountCop);

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-4">
        <Link to={backPath} className={`font-sans text-sm ${linkClass}`}>
          ← {t(backLabelKey)}
        </Link>
      </div>

      <div className="rounded-lg border border-brand-700/20 bg-surface p-6">
        <div className="mb-6 flex flex-col gap-4 border-b border-brand-700/15 pb-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="font-heading text-heading-xl text-brand-950">{t('orders.detailTitle')}</h1>
            <p className="mt-1 font-sans text-sm text-brand-700">
              {t('orders.orderShort', { id: order.id.slice(0, 8) })}
            </p>
            <p className="font-sans text-sm text-brand-700">{new Date(order.createdAt).toLocaleString()}</p>
          </div>
          <div className="space-y-2 text-left sm:text-right">
            <div>
              <p className="font-sans text-sm text-brand-700">{t('orders.detailPaymentStatus')}</p>
              <Badge variant={statusVariant(order.status)}>{statusLabel(order.status, t)}</Badge>
            </div>
            {order.status === 'PAGADA' && (
              <div>
                <p className="font-sans text-sm text-brand-700">{t('orders.detailFulfillmentStatus')}</p>
                <p className="font-sans font-medium text-brand-950">
                  {operationalOrderStatusLabel(order.fulfillmentStatus, t)}
                </p>
              </div>
            )}
          </div>
        </div>

        <h2 className="mb-3 font-heading text-heading-md text-brand-950">{t('orders.itemsHeading')}</h2>
        <ul className="mb-6 divide-y divide-brand-700/15">
          {order.items.map((item) => (
            <li key={item.id} className="py-4 first:pt-0">
              <div className="flex flex-col gap-1 font-sans">
                <p className="font-medium text-brand-950">{item.productName}</p>
                <p className="text-sm text-brand-700">
                  {t('orders.seller')}: {item.seller.displayName || item.seller.id.slice(0, 8)}
                </p>
                <p className="text-sm text-brand-700">
                  {t('common.quantity')}: {item.quantity}
                </p>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-brand-700">
                  <span>
                    {t('orders.unitCop')}: {formatPrice(item.unitPriceCop, 'COP')}
                  </span>
                  {item.currencyDisplay !== 'COP' && (
                    <span>
                      {t('orders.unitDisplay')}: {formatPrice(item.unitPriceDisplay, item.currencyDisplay)}
                    </span>
                  )}
                </div>
                <div className="mt-1 text-sm font-medium text-brand-950">
                  {t('orders.lineTotalCop')}: {formatPrice(lineTotalCop(item), 'COP')}
                  {item.currencyDisplay !== 'COP' && (
                    <span className="ml-2 font-normal text-brand-700">
                      ({formatPrice(lineTotalDisplay(item), item.currencyDisplay)})
                    </span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>

        <div className="space-y-2 border-t border-brand-700/15 pt-4">
          <div className="flex justify-between font-heading text-heading-md text-brand-950">
            <span>
              {order.sellerSubtotalCop != null ? t('orders.sellerSubtotalCop') : t('orders.orderTotalCop')}
            </span>
            <span>{formatPrice(sellerViewTotal, 'COP')}</span>
          </div>
          {showDisplayTotal && order.sellerSubtotalCop == null && (
            <div className="flex justify-between font-sans text-sm text-brand-700">
              <span>{t('orders.orderTotalDisplay')}</span>
              <span>{formatPrice(order.displayAmount, order.displayCurrency)}</span>
            </div>
          )}
        </div>
      </div>

      {order.orderChangeLogs && order.orderChangeLogs.length > 0 && (
        <div className="mt-8 rounded-lg border border-brand-700/20 bg-surface p-6">
          <h2 className="mb-4 font-heading text-heading-md text-brand-950">{t('orders.changeHistoryHeading')}</h2>
          <ul className="space-y-3 font-sans text-sm">
            {order.orderChangeLogs.map((log) => (
              <li key={log.id} className="border-b border-brand-700/15 pb-3 last:border-0">
                <p className="text-xs text-brand-700">
                  {new Date(log.createdAt).toLocaleString()} · {log.actor.displayName || log.actor.email}
                </p>
                <p className="mt-1 text-brand-950">
                  {log.changeType === 'ORDER_STATUS' &&
                    log.fromOrderStatus &&
                    log.toOrderStatus &&
                    `${statusLabel(log.fromOrderStatus, t)} → ${statusLabel(log.toOrderStatus, t)}`}
                  {log.changeType === 'FULFILLMENT' &&
                    log.fromFulfillmentStatus &&
                    log.toFulfillmentStatus &&
                    `${operationalOrderStatusLabel(log.fromFulfillmentStatus, t)} → ${operationalOrderStatusLabel(log.toFulfillmentStatus, t)}`}
                </p>
                {log.comment && <p className="mt-1 italic text-brand-700">&quot;{log.comment}&quot;</p>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
